import type {
  IRepresentativeRequestData,
  TRepresentativeRequestType,
} from 'shared/services/createRepresentativeRequest';
import { isEmail, isRussianPhone } from 'shared/utils/validation';

/** Типы заявки и подписи — как `defaultDropItems` в `bsc_add_request_dialog.tsx` монолита */
export const REPRESENTATIVE_REQUEST_TYPES: Array<{ value: TRepresentativeRequestType; label: string }> = [
  { value: 'addBuilding', label: 'Добавить здание в каталог БЦ' },
  { value: 'becomeRepresentative', label: 'Стать официальным представителем' },
];

export interface IRepresentativeRequestValues {
  requestType: TRepresentativeRequestType;
  name: string;
  /** Цифры номера без `+7` */
  phone: string;
  email: string;
  buildingName: string;
  comment: string;
}

export type TRepresentativeRequestErrors = Partial<Record<'name' | 'phone' | 'email', string>>;

export const INITIAL_REPRESENTATIVE_REQUEST: IRepresentativeRequestValues = {
  requestType: 'addBuilding',
  name: '',
  phone: '',
  email: '',
  buildingName: '',
  comment: '',
};

/** Комментарий есть в форме только у заявки представителя */
export const hasRequestComment = (requestType: TRepresentativeRequestType): boolean =>
  requestType === 'becomeRepresentative';

/**
 * Ошибки формы. В монолите — только непустые имя и телефон (пробелы считались значением); у нас имя без
 * пробелов по краям, телефон целиком, email — если заполнен
 */
export function validateRepresentativeRequest({
  name,
  phone,
  email,
}: IRepresentativeRequestValues): TRepresentativeRequestErrors {
  const errors: TRepresentativeRequestErrors = {};

  if (!name.trim()) {
    errors.name = 'Укажите имя и фамилию';
  }

  if (!phone) {
    errors.phone = 'Укажите телефон';
  } else if (!isRussianPhone(phone)) {
    errors.phone = 'Укажите телефон полностью';
  }

  if (email.trim() && !isEmail(email.trim())) {
    errors.email = 'Введите корректный адрес';
  }

  return errors;
}

/** Данные заявки из формы: значения без пробелов по краям, пустые — `null`, комментарий — только если поле видно */
export function getRepresentativeRequestData(values: IRepresentativeRequestValues): IRepresentativeRequestData {
  const optional = (value: string): string | null => value.trim() || null;

  return {
    requestType: values.requestType,
    name: values.name.trim(),
    phone: `7${values.phone}`,
    email: optional(values.email),
    buildingName: optional(values.buildingName),
    comment: hasRequestComment(values.requestType) ? optional(values.comment) : null,
  };
}
